import { useState, useRef, useEffect, useCallback } from "react";

const BASE = "/api";

// Chart.js loaded via CDN in index.html — accessed via window.Chart
const CHART_COLORS = [
  '#00d4aa','#0066ff','#ff6b35','#a855f7','#f59e0b',
  '#ec4899','#06b6d4','#84cc16','#ef4444','#8b5cf6'
];

const QUICK_QUESTIONS = [
  { icon:"📊", text:"Total production MTD by material" },
  { icon:"📅", text:"Daily production trend for Cadmium Ingots" },
  { icon:"🏭", text:"Which plant produced the most this month?" },
  { icon:"📦", text:"Show all dispatches MTD with quantities" },
  { icon:"🔄", text:"Top 5 materials by consumption MTD" },
  { icon:"📉", text:"Show closing stock for all materials" },
  { icon:"⚗️", text:"Cobalt plant production summary" },
  { icon:"🥇", text:"Which material has highest production FTD?" },
];

function escHtml(str) {
  return String(str)
    .replace(/&/g,'&amp;').replace(/</g,'&lt;')
    .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function fmtVal(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!isNaN(n)) return n.toLocaleString('en-IN', { maximumFractionDigits: 3 });
  return String(v);
}

// ── Chart component ───────────────────────────────────────────
function DprChart({ cid, type, title, labels, datasets }) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !window.Chart) return;
    if (chartRef.current) { chartRef.current.destroy(); }
    const isPie = type === 'pie' || type === 'doughnut';
    const ds = datasets.map((d, i) => {
      const col = CHART_COLORS[i % CHART_COLORS.length];
      const base = { label: d.label || '', data: d.data || [] };
      if (isPie) {
        base.backgroundColor = CHART_COLORS;
        base.borderColor     = CHART_COLORS.map(c => c + '80');
        base.borderWidth     = 1;
      } else {
        base.backgroundColor = type === 'line' ? col + '20' : col + 'cc';
        base.borderColor     = col;
        base.borderWidth     = type === 'line' ? 2 : 0;
        base.borderRadius    = type === 'bar' ? 4 : 0;
        base.fill            = type === 'line';
        base.tension         = 0.4;
        base.pointBackgroundColor = col;
        base.pointRadius     = 3;
      }
      return base;
    });
    chartRef.current = new window.Chart(canvasRef.current, {
      type,
      data: { labels: labels || [], datasets: ds },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: {
            display: isPie || ds.length > 1,
            labels: { color:'#475569', font:{ family:'monospace', size:11 }, padding:16 }
          },
          tooltip: {
            backgroundColor:'#111820', borderColor:'#1c2535', borderWidth:1,
            titleColor:'#eef3fa', bodyColor:'#c9d4e0',
            callbacks: {
              label: ctx => ' ' + (ctx.dataset.label ? ctx.dataset.label + ': ' : '') + Number(ctx.raw).toLocaleString('en-IN')
            }
          }
        },
        scales: isPie ? {} : {
          x: { ticks:{ color:'#7a8fa8', font:{ size:10 }, maxRotation:45 }, grid:{ color:'rgba(0,0,0,.04)' } },
          y: { ticks:{ color:'#7a8fa8', font:{ size:10 }, callback: v => {
            if (v >= 1000) return (v/1000).toFixed(1)+'K';
            return v;
          }}, grid:{ color:'rgba(0,0,0,.04)' } }
        }
      }
    });
    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [type, labels, datasets]);

  return (
    <div className="dpr-chart-wrap">
      {title && <div className="dpr-chart-title">{title}</div>}
      <div style={{ position:'relative', height:280 }}>
        <canvas ref={canvasRef}></canvas>
      </div>
    </div>
  );
}

// ── Table component ───────────────────────────────────────────
function DprTable({ columns, rows, totalRows }) {
  return (
    <div className="dpr-tbl-wrap">
      <div className="dpr-tbl-header">
        <span>📋 {columns.length} columns</span>
        <span>{(totalRows||rows.length).toLocaleString()} rows{totalRows > rows.length ? ` (showing ${rows.length})` : ''}</span>
      </div>
      <div className="dpr-tbl-scroll">
        <table className="dpr-table">
          <thead>
            <tr>{columns.map(c => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map(c => <td key={c} title={String(row[c] ?? '')}>{fmtVal(row[c])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── PDF download ──────────────────────────────────────────────
function downloadPDF(data, question) {
  const { columns=[], rows=[], answer='', highlight='', sql='' } = data;
  const date = new Date().toLocaleDateString('en-IN');
  const time = new Date().toLocaleTimeString('en-IN');

  // Build HTML for PDF
  let tableHtml = '';
  if (columns.length && rows.length) {
    const ths = columns.map(c => `<th>${escHtml(c)}</th>`).join('');
    const trs = rows.map((r,i) => {
      const tds = columns.map(c => `<td>${escHtml(fmtVal(r[c]))}</td>`).join('');
      return `<tr class="${i%2===0?'even':'odd'}">${tds}</tr>`;
    }).join('');
    tableHtml = `<table><thead><tr>${ths}</tr></thead><tbody>${trs}</tbody></table>`;
  }

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>RGTPL DPR Report</title>
<style>
  body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;margin:20px;}
  h1{font-size:18px;color:#1F4E79;border-bottom:2px solid #1F4E79;padding-bottom:8px;}
  h2{font-size:13px;color:#374151;margin-top:16px;}
  .meta{font-size:11px;color:#6B7280;margin-bottom:16px;}
  .highlight{background:#f0fdf4;border:1px solid #a7f3d0;border-radius:6px;padding:10px 14px;font-weight:bold;color:#065f46;margin-bottom:12px;}
  .answer{background:#F8FAFC;border:1px solid #E5E7EB;border-radius:6px;padding:10px 14px;margin-bottom:16px;line-height:1.6;}
  table{width:100%;border-collapse:collapse;font-size:11px;margin-top:8px;}
  th{background:#1F4E79;color:#fff;padding:7px 10px;text-align:left;}
  td{padding:6px 10px;border-bottom:1px solid #E5E7EB;}
  tr.even td{background:#F8FAFC;}
  .sql{font-family:monospace;font-size:10px;background:#F1F5F9;padding:10px;border-radius:4px;white-space:pre-wrap;word-break:break-all;margin-top:16px;}
  @media print{.no-print{display:none;}}
</style></head><body>
<h1>RGTPL Daily Production Report — Analytics</h1>
<div class="meta">Generated: ${date} ${time} &nbsp;|&nbsp; Query: ${escHtml(question)}</div>
${highlight ? `<div class="highlight">✦ ${escHtml(highlight)}</div>` : ''}
${answer ? `<div class="answer">${escHtml(answer)}</div>` : ''}
<h2>Data (${rows.length} rows)</h2>
${tableHtml}
<div class="sql"><strong>SQL:</strong>\n${escHtml(sql)}</div>
</body></html>`;

  const blob = new Blob([html], { type:'text/html' });
  const url  = URL.createObjectURL(blob);
  const w    = window.open(url, '_blank');
  if (w) setTimeout(() => { w.print(); URL.revokeObjectURL(url); }, 800);
}

// ── Excel download ────────────────────────────────────────────
function downloadExcel(data, question) {
  const { columns=[], rows=[] } = data;
  // Build CSV with BOM for Excel compatibility
  const bom = '\uFEFF';
  const header = columns.map(c => `"${c}"`).join(',');
  const body   = rows.map(r => columns.map(c => {
    const v = r[c] ?? '';
    return typeof v === 'string' ? `"${v.replace(/"/g,'""')}"` : v;
  }).join(',')).join('\n');
  const csv  = bom + `"RGTPL DPR Analytics Report"\n"Query: ${question}"\n"Generated: ${new Date().toLocaleString('en-IN')}"\n\n${header}\n${body}`;
  const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `DPR_Analytics_${new Date().toISOString().slice(0,10)}.csv`;
  a.click(); URL.revokeObjectURL(url);
}

// ── Bot Message component ─────────────────────────────────────
function BotMessage({ data, question, msgId }) {
  const [viewType, setViewType] = useState(data.type || 'table');
  const chartTypes = ['bar','line','pie','doughnut'];
  const hasChart = chartTypes.includes(data.type) && data.chart_labels && data.chart_datasets;
  const hasTable = data.columns?.length > 0 && data.rows?.length > 0;

  if (data.error) {
    return (
      <div className="dpr-bubble dpr-bubble-error">
        ❌ <strong>Error:</strong> {data.error}
      </div>
    );
  }

  return (
    <div className="dpr-bubble">
      {data.highlight && (
        <div className="dpr-highlight">
          <span style={{fontSize:16}}>✦</span>
          {data.highlight}
        </div>
      )}
      {data.answer && (
        <p style={{fontSize:13,lineHeight:1.7,color:'#1e293b',marginBottom:8}}>{data.answer}</p>
      )}

      {/* View toggle */}
      {(hasChart || hasTable) && (
        <div className="dpr-view-toggle">
          {hasChart && <>
            {['bar','line','pie','doughnut'].map(t => (
              <button key={t} className={`dpr-vt-btn ${viewType===t?'active':''}`}
                onClick={() => setViewType(t)}>
                {t==='bar'?'📊':t==='line'?'📈':t==='pie'?'🥧':'🍩'} {t.charAt(0).toUpperCase()+t.slice(1)}
              </button>
            ))}
          </>}
          {hasTable && (
            <button className={`dpr-vt-btn ${viewType==='table'?'active':''}`}
              onClick={() => setViewType('table')}>
              📋 Table
            </button>
          )}
        </div>
      )}

      {/* Chart */}
      {hasChart && viewType !== 'table' && (
        <DprChart cid={`chart-${msgId}`} type={viewType}
          title={data.chart_title} labels={data.chart_labels} datasets={data.chart_datasets} />
      )}

      {/* Table */}
      {hasTable && viewType === 'table' && (
        <DprTable columns={data.columns} rows={data.rows} totalRows={data.total_rows} />
      )}

      {/* SQL + Download buttons */}
      {data.sql && (
        <SqlDownloadBar sql={data.sql} data={data} question={question} />
      )}
    </div>
  );
}

function SqlDownloadBar({ sql, data, question }) {
  const [showSql, setShowSql] = useState(false);
  return (
    <div style={{marginTop:10}}>
      <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
        <button className="dpr-sql-chip" onClick={() => setShowSql(s => !s)}>
          ⌗ {showSql ? 'Hide' : 'View'} SQL
        </button>
        <button className="dpr-dl-btn" onClick={() => downloadExcel(data, question)}>
          ⬇ Excel
        </button>
        <button className="dpr-dl-btn dpr-dl-pdf" onClick={() => downloadPDF(data, question)}>
          🖨 PDF
        </button>
      </div>
      {showSql && (
        <pre className="dpr-sql-box">{sql}</pre>
      )}
    </div>
  );
}

// ── Main Chatbot component ────────────────────────────────────
export default function Chatbot() {
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const chatRef  = useRef(null);
  const inputRef = useRef(null);
  const msgIdRef = useRef(0);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const ask = useCallback(async (question) => {
    if (!question.trim() || loading) return;
    setInput('');
    const id = ++msgIdRef.current;
    setMessages(m => [...m, { id, role:'user', text:question }, { id:id+0.5, role:'typing' }]);
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/chat`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ question })
      });
      const data = await r.json();
      setMessages(m => m.filter(x => x.role !== 'typing').concat({ id:id+1, role:'bot', data, question }));
    } catch(e) {
      setMessages(m => m.filter(x => x.role !== 'typing').concat({ id:id+1, role:'bot', data:{error:'Network error: '+e.message}, question }));
    } finally { setLoading(false); inputRef.current?.focus(); }
  }, [loading]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(input); }
  };

  return (
    <div className="dpr-layout">
      {/* Sidebar */}
      <aside className="dpr-sidebar">
        <div className="dpr-sidebar-logo">
          <div className="dpr-logo-sq">HZ</div>
          <div>
            <div className="dpr-logo-title">RGTPL Analytics</div>
            <div className="dpr-logo-sub">SQL · AI · GPT-4o mini</div>
          </div>
        </div>

        <div className="dpr-sidebar-section">
          <div className="dpr-section-label">Quick Questions</div>
          {QUICK_QUESTIONS.map((q, i) => (
            <button key={i} className="dpr-quick-btn" onClick={() => ask(q.text)}>
              <span className="dpr-quick-icon">{q.icon}</span>
              <span className="dpr-quick-text">{q.text}</span>
            </button>
          ))}
        </div>

        <div className="dpr-sidebar-tables">
          <div className="dpr-section-label">Connected Tables</div>
          {[
            {name:'sap_movements',  dot:'#0ea5e9', cols:'9 cols'},
            {name:'closing_stock',  dot:'#3b82f6', cols:'9 cols'},
            {name:'uploads',        dot:'#f97316', cols:'5 cols'},
          ].map(t => (
            <div key={t.name} className="dpr-table-chip">
              <div className="dpr-table-dot" style={{background:t.dot}}></div>
              <div className="dpr-table-name">{t.name}</div>
              <div className="dpr-table-count">{t.cols}</div>
            </div>
          ))}
        </div>
      </aside>

      {/* Main */}
      <div className="dpr-main">
        <div className="dpr-topbar">
          <div>
            <div className="dpr-topbar-title">DPR Production Intelligence</div>
            <div className="dpr-topbar-sub">Ask anything about production, consumption, dispatches, stock</div>
          </div>
          <div className="dpr-status-pill">
            <div className="dpr-status-dot"></div> Live
          </div>
        </div>

        <div className="dpr-chat-area" ref={chatRef}>
          {messages.length === 0 && (
            <div className="dpr-welcome">
              <div className="dpr-welcome-icon">⚡</div>
              <h2>RGTPL DPR Analytics AI</h2>
              <p>Ask me anything about production, consumption, dispatches, stock levels, material quantities and trends. I'll respond with charts, tables, and summaries.</p>
              <div className="dpr-welcome-tags">
                <span className="dpr-welcome-tag">Plain English questions</span>
                <span className="dpr-welcome-tag">Auto charts & tables</span>
                <span className="dpr-welcome-tag">Excel & PDF download</span>
                <span className="dpr-welcome-tag">Live database</span>
              </div>
            </div>
          )}

          {messages.map(msg => (
            <div key={msg.id} className={`dpr-msg ${msg.role === 'user' ? 'dpr-msg-user' : 'dpr-msg-bot'}`}>
              <div className="dpr-msg-avatar">
                {msg.role === 'user' ? '👤' : msg.role === 'typing' ? '⚡' : '⚡'}
              </div>
              <div className="dpr-msg-body">
                {msg.role === 'user' && (
                  <div className="dpr-bubble dpr-bubble-user">{msg.text}</div>
                )}
                {msg.role === 'typing' && (
                  <div className="dpr-typing">
                    <div className="dpr-dot"></div>
                    <div className="dpr-dot"></div>
                    <div className="dpr-dot"></div>
                  </div>
                )}
                {msg.role === 'bot' && (
                  <BotMessage data={msg.data} question={msg.question} msgId={msg.id} />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="dpr-input-area">
          <div className="dpr-input-wrap">
            <textarea ref={inputRef} className="dpr-input" rows={1}
              placeholder="Ask about production, stock, dispatches, materials…"
              value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
              style={{resize:'none'}}
            />
            <button className="dpr-send-btn" onClick={() => ask(input)} disabled={loading || !input.trim()}>
              ➤
            </button>
          </div>
          <div className="dpr-input-hint">Press Enter to send · Shift+Enter for new line</div>
        </div>
      </div>
    </div>
  );
}
